import { useEffect, useRef } from 'react';
import { useField } from 'formik';

import { useTeams } from '@/react/portainer/users/teams/queries/useTeams';
import { useAddTeamMutation } from '@/react/portainer/users/teams/queries/useAddTeamMutation';
import { useTeamsDenyingPortainerAccess } from '@/react/portainer/users/teams/queries/useTeamsDenyingPortainerAccess';
import { DENY_PORTAINER_ACCESS_TOOLTIP } from '@/react/portainer/users/teams/denyPortainerAccess';
import { notifySuccess } from '@/portainer/services/notifications';

import { FormControl } from '@@/form-components/FormControl';
import { FormSection } from '@@/form-components/FormSection';
import { TeamsSelector } from '@@/TeamsSelector';
import { InlineLoader } from '@@/InlineLoader';
import { Alert } from '@@/Alert';
import { Link } from '@@/Link';
import { SwitchField } from '@@/form-components/SwitchField';

export function AssignTeamsStep({
  showDenyPortainerAccess,
}: {
  showDenyPortainerAccess: boolean;
}) {
  const teamsQuery = useTeams();
  const createTeamMutation = useAddTeamMutation();
  const denyingTeamIds = useTeamsDenyingPortainerAccess();
  const [teamsField, , teamsHelpers] = useField<number[]>('teams');
  // Per-team override, keyed by team id: a team absent here falls back to
  // showing its current stored state, so an untouched toggle reflects reality.
  const [denyPortainerAccessField, , denyPortainerAccessHelpers] = useField<
    Record<number, boolean>
  >('denyPortainerAccess');

  // Read the selection through a ref so the async onSuccess doesn't overwrite a
  // selection made while the create was in flight.
  const teamsValueRef = useRef(teamsField.value);
  useEffect(() => {
    teamsValueRef.current = teamsField.value;
  }, [teamsField.value]);

  if (teamsQuery.isError) {
    return <Alert color="error">Unable to load teams.</Alert>;
  }

  const teamsById = new Map(
    (teamsQuery.data ?? []).map((team) => [team.Id, team])
  );

  return (
    <FormSection title="Assign teams">
      <p className="mb-4 text-sm text-[var(--text-muted-color)]">
        Grant teams access to this add-on. No teams are needed if only
        administrators will be allowed to access it. You can create and manage
        teams in the{' '}
        <Link
          to="portainer.teams"
          target="_blank"
          rel="noopener noreferrer"
          data-cy="addon-assign-teams-view-link"
        >
          Teams view
        </Link>
        , or type a name below to create one on the fly.
      </p>

      <FormControl label="Teams" size="vertical" inputId="addon-assign-teams">
        {teamsQuery.isLoading ? (
          <InlineLoader>Loading teams...</InlineLoader>
        ) : (
          <TeamsSelector
            teams={teamsQuery.data ?? []}
            value={teamsField.value}
            onChange={(value) => {
              teamsHelpers.setValue(value);
              denyPortainerAccessHelpers.setValue(
                Object.fromEntries(
                  Object.entries(denyPortainerAccessField.value).filter(
                    ([teamId]) => value.includes(Number(teamId))
                  )
                )
              );
            }}
            inputId="addon-assign-teams"
            dataCy="addon-assign-teams-selector"
            placeholder="Select one or more teams"
            isCreatable
            isLoading={createTeamMutation.isLoading}
            onCreateOption={handleCreateTeam}
          />
        )}
      </FormControl>

      {showDenyPortainerAccess &&
        !teamsQuery.isLoading &&
        teamsField.value.length > 0 && (
          <div className="mt-4 flex flex-col gap-2">
            {teamsField.value.map((teamId) => {
              const checked =
                denyPortainerAccessField.value[teamId] ??
                denyingTeamIds.includes(teamId);

              return (
                <SwitchField
                  key={teamId}
                  label={`Restrict "${
                    teamsById.get(teamId)?.Name ?? teamId
                  }" to add-ons only`}
                  tooltip={DENY_PORTAINER_ACCESS_TOOLTIP}
                  checked={checked}
                  onChange={(value) =>
                    denyPortainerAccessHelpers.setValue({
                      ...denyPortainerAccessField.value,
                      [teamId]: value,
                    })
                  }
                  data-cy={`addon-assign-teams-deny-portainer-access-toggle-${teamId}`}
                />
              );
            })}
          </div>
        )}
    </FormSection>
  );

  function handleCreateTeam(name: string) {
    createTeamMutation.mutate(
      {
        name,
        leaders: [],
        denyPortainerAccess: false,
      },
      {
        onSuccess: (team) => {
          // Select the new team so it is granted access on save (or on the
          // next wizard step during install).
          teamsHelpers.setValue([...teamsValueRef.current, team.Id]);
          notifySuccess('Team created', `Team "${team.Name}" created`);
        },
      }
    );
  }
}
